import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Sheet, 
  SheetContent, 
  SheetDescription, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger 
} from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2, Tag, X, Award, Leaf, Heart } from "lucide-react";
import paymentMethodsImg from "@/assets/payment-methods.png";
import bundleHero from "@/assets/products/bundle-hero.png";
import { useCartStore, type CartItem } from "@/stores/cartStore";
import { FreeShippingBar } from "@/components/FreeShippingBar";
import { useLanguage, isRTL } from "@/lib/i18n";
import { formatPrice, calcDiscountPercent } from "@/lib/shopify";
import { getFixedPrice, formatFixedPrice, getFixedPriceTotal } from "@/lib/fixedPrices";
import { trackInitiateCheckout, generateEventId } from "@/lib/facebookPixel";
import { syncTrackingAttributesToCart } from "@/stores/cartStore";
import { trackGA4InitiateCheckout } from "@/lib/googleAnalytics";
import { getProductImages } from "@/lib/productImages";
import { getProductType } from "@/lib/productRegistry";
import { toast } from "sonner";

export function CartDrawer() {
  const { t, currency } = useLanguage();
  const { 
    items, 
    isLoading, 
    isSyncing,
    isCartOpen,
    discountCode,
    discountedTotal,
    discountedSubtotal,
    discountedVariantIds,
    openCart,
    closeCart,
    updateQuantity, 
    removeItem, 
    getCheckoutUrl, 
    syncCart,
    getTotalItems,
    getTotalPrice,
    applyDiscount,
    removeDiscount,
  } = useCartStore();
  
  const [couponInput, setCouponInput] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const totalItems = getTotalItems();
  const totalPrice = getTotalPrice(); // CAD total from Shopify (used for discount ratio)
  const displayCurrency = currency || 'USD';

  // Fixed-price total in display currency
  const fixedTotal = getFixedPriceTotal(
    items.map(i => ({ handle: i.product.node.handle, quantity: i.quantity })),
    displayCurrency
  );

  // Map Shopify-discount ratio to fixed display total (uses subtotal in same Shopify currency when available)
  const discountBaseTotal = discountedSubtotal ?? totalPrice;
  const displayDiscountedTotal = (discountedTotal !== null && discountBaseTotal > 0 && discountedTotal < discountBaseTotal)
    ? Math.round(fixedTotal * (discountedTotal / discountBaseTotal))
    : null;
  const displaySavings = displayDiscountedTotal !== null ? Math.max(fixedTotal - displayDiscountedTotal, 0) : null;
  const itemsUntilPromo = Math.max(3 - totalItems, 0);

  // Sync cart with Shopify when drawer opens
  useEffect(() => { 
    if (isCartOpen) {
      syncCart();
    }
  }, [isCartOpen, syncCart]);

  // Get translated title for a cart item
  const getItemTitle = (item: CartItem): string => {
    const handle = item.product.node.handle;
    const productType = getProductType(handle);
    const key = `productContent.title.${productType}`;
    const translated = t(key);
    return translated !== key ? translated : item.product.node.title;
  };

  const handleCheckout = async () => {
    if (isCheckingOut) return;
    setIsCheckingOut(true);
    try {
    let checkoutUrl = getCheckoutUrl();
    if (checkoutUrl) {
      // Re-sync tracking attributes right before checkout so fbc/fbp are fresh
      const { cartId } = useCartStore.getState();
      if (cartId) {
        await syncTrackingAttributesToCart(cartId);
      }

      if (items.length > 0) {
        const checkoutData = {
          items: items.map(item => {
            const itemFp = getFixedPrice(item.product.node.handle, displayCurrency);
            return {
              productId: item.product.node.id,
              variantId: item.variantId,
              title: item.product.node.title,
              price: itemFp ? itemFp.sale : parseFloat(item.price.amount),
              quantity: item.quantity,
            };
          }),
          totalValue: fixedTotal || totalPrice,
          currency: displayCurrency,
          cartId: undefined,
        };
        // Generate ONE shared event_id for both FB and GA4 — ensures dashboard dedup
        const icEventId = await generateEventId('InitiateCheckout', Date.now().toString());
        await Promise.all([
          trackInitiateCheckout(checkoutData, icEventId),
          trackGA4InitiateCheckout(checkoutData, icEventId),
        ]);
      }
      try {
        const fbclid = new URLSearchParams(window.location.search).get('fbclid')
          || localStorage.getItem('_fb_last_fbclid');
        if (fbclid) {
          const url = new URL(checkoutUrl);
          url.searchParams.set('fbclid', fbclid);
          checkoutUrl = url.toString();
        }
      } catch { /* ignore URL parse errors */ }
      window.open(checkoutUrl, '_blank');
      closeCart();
    }
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    const success = await applyDiscount(couponInput.trim().toUpperCase());
    if (success) {
      toast.success(t('cart.couponApplied'));
      setCouponInput('');
    } else {
      toast.error(t('cart.couponError'));
    }
  };

  const handleRemoveCoupon = async () => {
    await removeDiscount();
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      openCart();
    } else {
      closeCart();
    }
  };

  // Trust Badges + Payment Methods (shared between empty and full cart)
  const trustAndPayment = (
    <div className="flex-shrink-0 space-y-3 pt-4 mt-auto">
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: Award, labelKey: 'product.moneyBack' },
          { icon: Leaf, labelKey: 'product.crueltyFree' },
          { icon: Heart, labelKey: 'product.dermatologistTested' },
        ].map(({ icon: Icon, labelKey }) => (
          <div key={labelKey} className="flex flex-col items-center gap-1.5 text-center px-1">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon className="w-4 h-4 text-primary" />
            </div>
            <span className="text-[10px] leading-tight text-muted-foreground font-medium">{t(labelKey)}</span>
          </div>
        ))}
      </div>
      <div className="text-center space-y-2">
        <p className="text-[10px] text-muted-foreground">{t('product.payWith')}</p>
        <img src={paymentMethodsImg} alt="Payment methods" className="w-full max-w-[280px] mx-auto object-contain" />
      </div>
    </div>
  );

  return (
    <Sheet open={isCartOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Shopping cart">
          <ShoppingCart className="h-5 w-5" />
          {totalItems > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-primary text-primary-foreground">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side={isRTL(useLanguage.getState().language) ? 'left' : 'right'} className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="text-xl font-bold">{t('cart.title')}</SheetTitle>
          <SheetDescription>
            {totalItems === 0 
              ? t('cart.empty') 
              : t('cart.itemCount', { count: totalItems })}
          </SheetDescription>
        </SheetHeader>
        
        {/* B2G1 Promo Message */}
        {totalItems > 0 && totalItems < 3 && (
          <div className="flex-shrink-0 bg-accent/10 text-foreground text-center text-xs py-2 px-3 rounded-lg font-medium border border-accent/20">
            {t('promo.b2g1CartHint', { count: itemsUntilPromo })}
          </div>
        )}
        {totalItems >= 3 && (
          <div className="flex-shrink-0 bg-emerald-50 text-emerald-700 text-center text-xs py-2 px-3 rounded-lg font-semibold border border-emerald-200 space-y-0.5">
            <div>{t('promo.b2g1Cart')}</div>
            {displaySavings !== null && displaySavings > 0 && (
              <div className="text-emerald-600 font-bold">{t('promo.b2g1Saving', { amount: formatFixedPrice(displaySavings, displayCurrency) })}</div>
            )}
          </div>
        )}

        {/* Free Shipping Progress — uses fixed prices */}
        {items.length > 0 && (
          <FreeShippingBar items={items.map(i => ({ handle: i.product.node.handle, quantity: i.quantity }))} />
        )}

        <div className="flex flex-col flex-1 pt-4 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingCart className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">{t('cart.empty')}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-2 min-h-0 space-y-4">
                {items.map((item) => {
                  const fp = getFixedPrice(item.product.node.handle, displayCurrency);
                  return (
                    <div key={item.variantId} className="flex gap-4 p-3 bg-secondary/30 rounded-lg">
                      <div className="w-20 h-20 bg-secondary/30 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                        {(() => {
                          const localImages = getProductImages(item.product.node.handle);
                          const localHero = localImages.length > 0 ? localImages[0] : null;
                          const shopifyImg = item.product.node.images?.edges?.[0]?.node;
                          if (localHero) {
                            return <img src={localHero.src} alt={getItemTitle(item)} className="w-full h-full object-cover" />;
                          } else if (shopifyImg) {
                            return <img src={shopifyImg.url} alt={getItemTitle(item)} className="w-full h-full object-cover" />;
                          }
                          return <span className="text-3xl">🌸</span>;
                        })()}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm leading-snug">
                          {getItemTitle(item)}
                        </h4>
                        {discountedVariantIds.includes(item.variantId) && (
                          <span className="inline-block mt-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                            {t('promo.freeItem')}
                          </span>
                        )}
                        {item.variantTitle !== 'Default Title' && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {item.selectedOptions.map(opt => opt.value).join(' • ')}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <p className="font-semibold text-primary">
                            {fp
                              ? formatFixedPrice(fp.sale, displayCurrency)
                              : formatPrice(item.price.amount, item.price.currencyCode, currency)
                            }
                          </p>
                          {fp && fp.original > fp.sale && (
                            <span className="text-xs text-muted-foreground line-through">
                              {formatFixedPrice(fp.original, displayCurrency)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 text-muted-foreground hover:text-destructive" 
                          onClick={() => removeItem(item.variantId)}
                          disabled={isLoading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        
                        <div className="flex items-center gap-1 bg-background rounded-md border">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7" 
                            onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                            disabled={isLoading}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7" 
                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                            disabled={isLoading}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Cross-sell: suggest Bundle when cart has only single items */}
                <CartCrossSell items={items} displayCurrency={displayCurrency} />
              </div>
              
              <div className="flex-shrink-0 space-y-4 pt-4 border-t mt-4">
                {/* Coupon Input */}
                <div className="space-y-2">
                  {discountCode ? (
                    <div className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-emerald-600" />
                        <span className="font-medium text-emerald-700">{discountCode}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-emerald-600 hover:text-emerald-800"
                        onClick={handleRemoveCoupon}
                        disabled={isLoading}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder={t('cart.couponPlaceholder')}
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value)}
                        className="flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                      />
                      <Button
                        variant="outline"
                        onClick={handleApplyCoupon}
                        disabled={isLoading || !couponInput.trim()}
                        className="flex-shrink-0"
                      >
                        {t('cart.applyCoupon')}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">{t('cart.total')}</span>
                  <div className="flex items-center gap-2">
                    {displayDiscountedTotal !== null && (
                      <span className="text-base text-muted-foreground line-through">
                        {formatFixedPrice(fixedTotal, displayCurrency)}
                      </span>
                    )}
                    <span className="text-2xl font-bold text-primary">
                      {displayDiscountedTotal !== null
                        ? formatFixedPrice(displayDiscountedTotal, displayCurrency)
                        : formatFixedPrice(fixedTotal, displayCurrency)
                      }
                    </span>
                  </div>
                </div>
                
                <Button 
                  onClick={handleCheckout} 
                  className="w-full bg-gradient-button shadow-pink hover:opacity-90 transition-opacity" 
                  size="lg" 
                  disabled={items.length === 0 || isLoading || isSyncing}
                >
                  {isLoading || isSyncing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 me-2" />
                      {t('cart.checkout')}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}

          {/* Trust Badges + Payment Methods — always visible */}
          {trustAndPayment}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const BUNDLE_HANDLE = 'complete-pdrn-trio-bundle';

function CartCrossSell({ items, displayCurrency }: { items: CartItem[]; displayCurrency: string }) {
  const { t } = useLanguage();
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);

  // Only show if cart has items, none are the bundle
  const hasBundle = items.some(item => getProductType(item.product.node.handle) === 'bundle');
  if (hasBundle || items.length === 0) return null;

  const bundleFp = getFixedPrice(BUNDLE_HANDLE, displayCurrency);
  if (!bundleFp) return null;

  const discountPct = calcDiscountPercent(bundleFp.sale, bundleFp.original);
  const translatedTitle = t('productContent.title.bundle');
  const displayTitle = translatedTitle !== 'productContent.title.bundle' ? translatedTitle : 'Complete PDRN Trio Bundle';

  const handleQuickAdd = async () => {
    // We need variant ID from Shopify — fetch the product
    const { fetchProductByHandle } = await import('@/lib/shopify');
    try {
      const product = await fetchProductByHandle(BUNDLE_HANDLE);
      if (!product) return;
      const variant = product.variants.edges[0]?.node;
      if (!variant) return;
      await addItem({
        product: { node: product as any },
        variantId: variant.id,
        variantTitle: variant.title,
        price: variant.price,
        quantity: 1,
        selectedOptions: variant.selectedOptions || [],
      });
    } catch (err) {
      console.error('Cross-sell add error:', err);
    }
  };

  return (
    <div className="mt-3 p-3 bg-primary/5 rounded-xl border border-primary/15">
      <p className="text-xs font-semibold text-foreground mb-2">{t('cart.completeRoutine')}</p>
      <div className="flex items-center gap-3">
        <img src={bundleHero} alt={displayTitle} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">{displayTitle}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-xs font-bold text-primary">{formatFixedPrice(bundleFp.sale, displayCurrency)}</span>
            <span className="text-[10px] text-muted-foreground line-through">{formatFixedPrice(bundleFp.original, displayCurrency)}</span>
            {discountPct > 0 && (
              <Badge className="bg-destructive text-destructive-foreground border-0 text-[9px] px-1 py-0">
                {t('cart.bundleSave', { percent: discountPct })}
              </Badge>
            )}
          </div>
        </div>
        <Button
          size="sm"
          onClick={handleQuickAdd}
          disabled={isLoading}
          className="bg-primary text-primary-foreground text-xs px-3 h-8 flex-shrink-0"
        >
          {t('cart.quickAdd')}
        </Button>
      </div>
    </div>
  );
}
